import React, { useCallback, useEffect, useMemo } from 'react';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { Button } from '@frontend/shadcn/components/ui/button';
import { P } from '@frontend/shadcn/components/typography/p';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { ArrowDownToLineIcon, CircleExclamationIcon } from '@revfluence/fresh-icons/solid/esm';
import { ExpandableSearch, NameCell, Tag } from '@frontend/app/refresh-components';
import { PenToSquareIcon } from '@revfluence/fresh-icons/regular/esm';
import { ExportToCsv } from 'export-to-csv';
import { ChevronDownIcon, ChevronUpIcon, ArrowDownArrowUpIcon } from '@revfluence/fresh-icons/regular/esm';
import { useClientFeatureEnabled } from '@frontend/app/hooks';
import { ClientFeature } from '@frontend/app/constants';
import { CellWithTooltip } from '@frontend/applications/PaymentsApp/ReFreshPayment/components/CellWithTooltip';
import { statusColorMapper as paymentStatusColorMapper } from '@frontend/applications/PaymentsApp/ReFreshPayment/constants';
import { formatPaymentDate } from '@frontend/applications/PaymentsApp/ReFreshPayment/utils/formatPaymentDate';
import { PaymentStatus } from '@services/budget-allocation/utils/getPaymentStatus';
import { ColumnSelectorModal, DataTable, TablePagination } from '@frontend/app/refresh-components/DataTableV2';
import { Brief, useBriefs } from '../context/BriefsContext';

type TableColumnDef = ColumnDef<Brief> & {
  accessorKey?: string;
  meta?: {
    label: string;
  };
};

interface BadgeItem {
  id: string;
  name: string;
}

const renderLimitedBadges = (items: BadgeItem[], limit: number) => {
  if (!items?.length) return '-';
  const visibleItems = items.slice(0, limit);
  const remainingCount = items.length - limit;

  return (
    <div className="flex flex-wrap gap-1">
      {visibleItems.map((item) => (
        <Tag key={item.id} label={item.name} bgColor="bg-grey-2" textColor="text-grey-6" />
      ))}
      {remainingCount > 0 && (
        <Tag label={`+${remainingCount} more`} bgColor="bg-grey-2" textColor="text-grey-6" />
      )}
    </div>
  );
};

// Updated SortButton with right-aligned sort icons
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const SortButton = ({ column, children }: { column: any; children: React.ReactNode }) => {
  const isSorted = column.getIsSorted();
  const [isHovering, setIsHovering] = React.useState(false);

  // Only allow sorting for specific columns
  const sortableColumns = ['member', 'project', 'createdDate', 'deliverables', 'payment', 'amountPaid', 'updatedDate'];
  const isSortable = sortableColumns.includes(column.id);

  if (!isSortable) {
    return <div className="w-full">{children}</div>;
  }

  const handleSort = () => {
    if (isSorted === 'asc') {
      column.toggleSorting(true); // Switch to desc
    } else if (isSorted === 'desc') {
      column.clearSorting(); // Clear sorting (no sort)
    } else {
      column.toggleSorting(false); // Switch to asc
    }
  };

  return (
    <Button
      variant="ghost"
      className="px-2 hover:bg-transparent w-full"
      onClick={handleSort}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="flex w-full justify-between items-center">
        <span>{children}</span>
        <span className="h-3 w-3 flex items-center justify-center">
          {isSorted === 'asc' && <ChevronUpIcon className="h-3 w-3" />}
          {isSorted === 'desc' && <ChevronDownIcon className="h-3 w-3" />}
          {!isSorted && isHovering && <ArrowDownArrowUpIcon className="h-3 w-3" />}
        </span>
      </div>
    </Button>
  );
};

const exportTransactionsToCSV = (transactions: Brief[]) => {
  const formattedTransactions = transactions.map((transaction) => ({
    member: transaction.member?.name || '',
    paymentDate: transaction.createdDate ? formatPaymentDate(transaction.createdDate) : '',
    amount: transaction.payment ?? '',
    status: transaction.status || '',
    project: transaction.project?.name || '',
    deliverables: transaction.deliverables || '',
    usageRights: transaction.usageRights || '',
    lastReviewer: transaction.lastReviewer || '',
    budgets: transaction.budgets?.map((b) => b.budgetName).join(', ') || '',
    fiscalYear: transaction.fiscalYear || '',
  }));

  const options = {
    fieldSeparator: ',',
    quoteStrings: '"',
    decimalSeparator: '.',
    showLabels: true,
    showTitle: false,
    title: 'Briefs',
    useTextFile: false,
    useBom: true,
    useKeysAsHeaders: true,
  };

  const csvExporter = new ExportToCsv(options);
  csvExporter.generateCsv(formattedTransactions);
};

export function BriefsTable() {
  const isBudgetAllocationEnabled = useClientFeatureEnabled(ClientFeature.BUDGET_ALLOCATION);
  const {
    pageIndex,
    isLoading,
    data,
    totalCount,
    pageCount,
    rowSelection,
    selectedRows,
    setIsEditBriefDrawerOpen,
    setPageIndex,
    setSorting,
    setRowSelection,
    columnVisibility,
    setColumnVisibility,
    setFilter,
    pageSize,
    filter,
  } = useBriefs();

  // Move columns definition here and wrap in useMemo
  const columns = useMemo<TableColumnDef[]>(
    () => [
      {
        accessorKey: 'member',
        header: ({ column }) => <SortButton column={column}>Member</SortButton>,
        meta: { label: 'Member' },
        cell: ({ row }) => {
          const member = row.getValue<Brief['member']>('member');
          const memberName = member?.name || '';
          const memberProfilePicture = member?.profilePicture || '';

          return <NameCell name={memberName} imageUrl={memberProfilePicture} />;
        },
        size: 200,
        minSize: 200,
      },
      {
        accessorKey: 'project',
        header: ({ column }) => <SortButton column={column}>Project</SortButton>,
        meta: { label: 'Project' },
        cell: ({ row }) => {
          const project = row.getValue<Brief['project']>('project');
          return <CellWithTooltip value={project?.name || '-'} />;
        },
        size: 200,
        minSize: 200,
      },
      // TODO: Add actions column
      // {
      //   id: 'actions',
      //   header: 'Actions',
      //   meta: { label: 'Actions' },
      //   cell: ({ row }) => {
      //     return (
      //       <div className="text-left">
      //         <Button
      //           variant="link"
      //           size="sm"
      //           className="p-0"
      //           onClick={(e) => {
      //             e.stopPropagation();
      //             // Handle action button click
      //             console.log('Action clicked for row:', row.original);
      //           }}
      //         >
      //           View Brief
      //         </Button>
      //       </div>
      //     );
      //   },
      //   size: 80,
      //   minSize: 80,
      // },
      {
        accessorKey: 'status',
        header: 'Brief Status',
        meta: { label: 'Brief Status' },
        cell: ({ row }) => {
          const status = row.getValue<Brief['status']>('status');
          const statusColor = {
            'Review Edits': {
              bgColor: 'bg-yellow-2',
              textColor: 'text-[#16282D]',
            },
            Agreed: {
              bgColor: 'bg-green-2',
              textColor: 'text-[#16282D]',
            },
            'Waiting for Agreement': {
              bgColor: 'bg-blue-2',
              textColor: 'text-[#16282D]',
            },
            Canceled: {
              bgColor: 'bg-[#E5E8EB]',
              textColor: 'text-[#16282D]',
            },
          };
          const statusBgColor = statusColor[status]?.bgColor || 'bg-grey-2';
          const statusTextColor = statusColor[status]?.textColor || 'text-grey-6';

          return (
            <Tag
              label={status?.charAt(0).toUpperCase() + status?.slice(1) || '-'}
              bgColor={statusBgColor}
              textColor={statusTextColor}
            />
          );
        },
        size: 150,
        minSize: 150,
      },
      {
        accessorKey: 'createdDate',
        header: ({ column }) => <SortButton column={column}>Date Sent</SortButton>,
        meta: { label: 'Date Sent' },
        cell: ({ row }) => {
          const date = row.getValue<string>('createdDate');
          return date ? formatPaymentDate(date) : '-';
        },
        size: 150,
        minSize: 150,
      },
      {
        accessorKey: 'deliverables',
        header: ({ column }) => <SortButton column={column}>Deliverables</SortButton>,
        meta: { label: '# of Deliverables' },
        cell: ({ row }) => {
          const deliverables = row.getValue<number>('deliverables');
          return <div className="text-right">{deliverables || '-'}</div>;
        },
        size: 120,
        minSize: 120,
      },
      {
        accessorKey: 'payment',
        header: ({ column }) => <SortButton column={column}>Amount</SortButton>,
        meta: { label: 'Payment' },
        cell: ({ row }) => {
          const amount = row.getValue<number>('payment');
          return <div className="text-right">${amount?.toLocaleString() || '-'}</div>;
        },
        size: 100,
        minSize: 100,
      },
      {
        accessorKey: 'amountPaid',
        header: ({ column }) => <SortButton column={column}>Amount Paid</SortButton>,
        meta: { label: 'Amount Paid' },
        cell: ({ row }) => {
          const amount = row.getValue<number>('amountPaid');
          return <div className="text-right">${amount?.toLocaleString() || '-'}</div>;
        },
        size: 100,
        minSize: 100,
      },
      {
        accessorKey: 'paymentStatus',
        header: 'Payment Status',
        meta: { label: 'Payment Status' },
        cell: ({ row }) => {
          const status = row.getValue<Brief['paymentStatus']>('paymentStatus') as PaymentStatus;

          const statusBgColor = paymentStatusColorMapper[status]?.bg || 'bg-grey-2';
          const statusTextColor = paymentStatusColorMapper[status]?.text || 'text-grey-6';

          return (
            <Tag
              label={status?.charAt(0).toUpperCase() + status?.slice(1) || '-'}
              bgColor={statusBgColor}
              textColor={statusTextColor}
            />
          );
        },
        size: 150,
        minSize: 150,
      },
      {
        accessorKey: 'usageRights',
        header: 'Usage Rights',
        meta: { label: 'Usage Rights' },
        cell: ({ row }) => <CellWithTooltip value={row.getValue<string>('usageRights') || '-'} />,
        size: 150,
        minSize: 150,
      },
      {
        accessorKey: 'lastReviewer',
        header: 'Last Reviewer',
        meta: { label: 'Last Reviewer' },
        cell: ({ row }) => <CellWithTooltip value={row.getValue<string>('lastReviewer') || '-'} />,
        size: 150,
        minSize: 150,
      },
      {
        accessorKey: 'updatedDate',
        header: ({ column }) => <SortButton column={column}>Last Activity</SortButton>,
        meta: { label: 'Last Activity' },
        cell: ({ row }) => {
          const date = row.getValue<string>('updatedDate');
          return date ? formatPaymentDate(date) : '-';
        },
        size: 150,
        minSize: 150,
      },
      ...(isBudgetAllocationEnabled
        ? [
            {
              accessorKey: 'budgets',
              header: 'Budgets',
              meta: { label: 'Budgets' },
              cell: ({ row }) => {
                const budgets = row.original.budgets;
                if (!budgets?.length) {
                  return (
                    <Tooltip>
                      <TooltipTrigger>
                        <CircleExclamationIcon className="text-grey-3 h-4 w-4" />
                      </TooltipTrigger>
                      <TooltipContent>No Budget Connected with this project</TooltipContent>
                    </Tooltip>
                  );
                }
                const formattedBudgets: BadgeItem[] = budgets.map((b) => ({ id: b.budgetId, name: b.budgetName }));
                return renderLimitedBadges(formattedBudgets, 2);
              },
              size: 300,
              minSize: 300,
            },
          ]
        : []),
      {
        accessorKey: 'fiscalYear',
        header: 'Fiscal Year',
        meta: { label: 'Fiscal Year' },
        cell: ({ row }) => {
          const fiscalYearLabel = row.original?.fiscalYearLabel;
          return <CellWithTooltip value={fiscalYearLabel || '-'} />;
        },
        size: 120,
        minSize: 120,
      },
    ],
    [isBudgetAllocationEnabled],
  );

  // Initialize column visibility on mount
  useEffect(() => {
    if (Object.keys(columnVisibility).length === 0) {
      const initialVisibility = columns.reduce(
        (acc, column) => ({
          ...acc,
          [column.accessorKey || '']: true,
        }),
        {},
      );
      setColumnVisibility(initialVisibility);
    }
  }, [columnVisibility, setColumnVisibility, columns]);

  // Handle column toggle
  const handleColumnToggle = useCallback(
    (columnId: string) => {
      setColumnVisibility((prev) => ({
        ...prev,
        [columnId]: !prev[columnId],
      }));
    },
    [setColumnVisibility],
  );

  // Filter visible columns - now depends on the memoized columns
  const visibleColumns = useMemo(() => columns.filter((column) => columnVisibility[column.accessorKey || '']), [
    columnVisibility,
    columns,
  ]);

  const handlePaginationChange = useCallback(
    (newPageIndex: number) => {
      setPageIndex(newPageIndex);
    },
    [setPageIndex],
  );

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      setSorting(newSorting);
      setPageIndex(0);
    },
    [setSorting, setPageIndex],
  );

  // Handle search
  const handleSearch = useCallback(
    (query: string, selectedOption?: string) => {
      const searchByMapping: Record<string, string> = {
        Member: 'member',
        Project: 'project',
      };

      const searchBy = searchByMapping[selectedOption || 'Member'];

      // Only update filter if we have a valid search query or if clearing the search
      if (query !== undefined) {
        setFilter({
          searchBy,
          searchQuery: query,
        });

        // Reset to first page when searching
        setPageIndex(0);
      }
    },
    [setFilter, setPageIndex],
  );

  // Handle row click
  const handleRowClick = useCallback(
    (row: Brief) => {
      // Clear existing selection and select new row in a single update
      setRowSelection((prev) => {
        // If the row is already selected, keep the selection
        if (prev[row.id]) return prev;
        // Otherwise, clear other selections and select this row
        return { [row.id]: true };
      });
      setIsEditBriefDrawerOpen(true);
    },
    [setRowSelection, setIsEditBriefDrawerOpen],
  );

  // Memoize the table component to prevent unnecessary re-renders
  const tableComponent = useMemo(
    () => (
      <DataTable
        columns={visibleColumns}
        data={data}
        onSortingChange={handleSortingChange}
        isLoading={isLoading}
        enableColumnResizing
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        getRowId={(row) => row.id.toString()}
        onRowClick={handleRowClick}
        className="cursor-pointer h-full"
        pinnedLeft={['select', 'member']}
      />
    ),
    [visibleColumns, data, handleSortingChange, isLoading, rowSelection, setRowSelection, handleRowClick],
  );

  return (
    <div className="w-full min-h-[calc(100vh-280px)] border-grey-2 rounded-2xl overflow-hidden border-solid border-[1px] flex flex-col">
      <div className="w-full bg-white flex justify-between items-center px-4 py-2 border-b border-grey-2">
        <div className="flex items-center gap-2">
          <P className="text-sm font-normal">{selectedRows.length} selected</P>
          <Tooltip>
            <TooltipTrigger>
              <Button
                disabled={!selectedRows.length}
                size="icon"
                variant="ghost"
                onClick={() => {
                  setIsEditBriefDrawerOpen(true);
                }}
              >
                <PenToSquareIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Update Brief in Bulk</TooltipContent>
          </Tooltip>
        </div>
        <div className="flex items-center gap-2">
          <ExpandableSearch
            options={['Member', 'Project']}
            placeholder="Search..."
            tooltipText="Search"
            onSearch={handleSearch}
            initialOption="Member"
            initialValue={filter.searchQuery || ''}
          />
          <Tooltip>
            <TooltipTrigger>
              <Button disabled={false} size="icon" variant="ghost" onClick={() => exportTransactionsToCSV(data)}>
                <ArrowDownToLineIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Export</TooltipContent>
          </Tooltip>
          <ColumnSelectorModal
            columns={columns.map((column) => ({
              id: column.accessorKey || '',
              label: column.meta?.label || 'Column',
              isVisible: columnVisibility[column.accessorKey || ''],
            }))}
            onColumnToggle={handleColumnToggle}
          />
          <TablePagination
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={totalCount}
            canPreviousPage={pageIndex > 0}
            canNextPage={pageIndex < pageCount - 1}
            onPaginationChange={handlePaginationChange}
          />
        </div>
      </div>
      <div className="flex-1 overflow-hidden">{tableComponent}</div>
    </div>
  );
}
